import { Avatar, AvatarFallback, AvatarImage, Badge, Button, Card, Input, Spinner } from '@fc/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Settings2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { type DirectoryEntry, listDirectory } from '../../lib/directory-api'

const statusVariant: Record<string, 'success' | 'neutral' | 'gold' | 'warning'> = {
  member: 'success',
  regular: 'gold',
  visitor: 'gold',
  inactive: 'neutral',
}

function initials(p: DirectoryEntry): string {
  return `${p.firstName.at(0) ?? ''}${p.lastName.at(0) ?? ''}`.toUpperCase() || '·'
}

export function DirectoryListPage() {
  const [search, setSearch] = useState('')
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['directory', search],
    queryFn: () => listDirectory({ q: search || undefined, pageSize: 100 }),
    placeholderData: keepPreviousData,
  })

  const entries = data?.items ?? []

  return (
    <div className="space-y-6">
      <PageHeader
        title="Directory"
        description="Search and browse everyone in your church community."
        actions={
          <Button asChild variant="secondary">
            <Link to="/app/directory/custom-fields">
              <Settings2 />
              Custom fields
            </Link>
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <Input
          placeholder="Search by name, nickname, or email…"
          className="max-w-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search directory"
        />
        {isFetching && <Spinner />}
      </div>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading directory…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load the directory.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : entries.length === 0 ? (
          <div className="py-20 text-center text-sm text-muted">
            {search ? `No matches for “${search}”.` : 'No people in the directory yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Email</th>
                  <th className="px-5 py-3 font-semibold">Phone</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Campus</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover"
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/app/directory/${p.id}`}
                        className="flex items-center gap-3 hover:text-secondary"
                      >
                        <Avatar className="size-8">
                          {p.photoUrl && <AvatarImage src={p.photoUrl} alt="" />}
                          <AvatarFallback>{initials(p)}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium">
                          {p.firstName} {p.lastName}
                          {p.nickname && <span className="ml-1 text-muted">“{p.nickname}”</span>}
                        </span>
                        {p.isMinor && <Badge variant="warning">Minor</Badge>}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-muted">{p.email ?? '—'}</td>
                    <td className="px-5 py-3 text-muted">{p.phone ?? '—'}</td>
                    <td className="px-5 py-3">
                      <Badge variant={statusVariant[p.status] ?? 'neutral'} className="capitalize">
                        {p.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-muted">{p.campus ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
